import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import moment from 'moment';
import { Bar } from 'react-chartjs-2';

import * as pluviometroActions from './pluviometroActions';
import TooltipWrapper from '../common/form/TooltipWrapper';
import { getConfig, setConfig } from '../configuracao/configLocal';

class PluviometroList extends Component {
  constructor(props) {
    super(props);

    this.state = {
      controladorSelecionado: (() => {
        const cont = getConfig('cont');
        const grupo = getConfig('grupo');
        if (!grupo || !cont || cont === '0' || cont === 'todos') {
          return null;
        }
        return String(cont);
      })(),
      autoRefresh: false,
      intervalId: null,
      filtros: {
        // Padrão: dia atual selecionado
        dataInicio: moment().format('YYYY-MM-DD'),
        dataFim: moment().format('YYYY-MM-DD'),
        agrupamento: '15min',
      },
    };

    this.handleControladorChange = this.handleControladorChange.bind(this);
    this.toggleAutoRefresh = this.toggleAutoRefresh.bind(this);
    this.atualizarLeitura = this.atualizarLeitura.bind(this);
    this.handleFiltroChange = this.handleFiltroChange.bind(this);
    this.buscarHistorico = this.buscarHistorico.bind(this);
    this.isHistoricoPronto = this.isHistoricoPronto.bind(this);
    this.syncControladorSelecionado = this.syncControladorSelecionado.bind(this);
  }

  isHistoricoPronto(controladorId) {
    const grupo = getConfig('grupo');
    const cont = controladorId !== undefined ? controladorId : getConfig('cont');
    const grupoValido = grupo && grupo !== '0' && grupo !== '';
    const contValido = cont && cont !== '0' && cont !== 'todos';
    return grupoValido && contValido;
  }

  getControladorSelecionadoNormalizado(controladorId) {
    const cont = controladorId !== undefined ? controladorId : getConfig('cont');
    if (!this.isHistoricoPronto(cont)) {
      return null;
    }
    return String(cont);
  }

  syncControladorSelecionado(callback) {
    const cont = getConfig('cont');
    if (cont === 'todos') {
      setConfig('cont', 0);
    }

    const next = this.getControladorSelecionadoNormalizado(cont);
    const current = this.getControladorSelecionadoNormalizado(this.state.controladorSelecionado);

    if (next === current) {
      if (callback) callback();
      return;
    }

    if (!next && current) {
      this.props.clearData();
    }

    this.setState({ controladorSelecionado: next }, callback);
  }

  componentDidMount() {
    this.syncControladorSelecionado(() => {
      if (this.isHistoricoPronto()) {
        this.carregarDados();
        if (this.props.activeTab === 'historico') {
          this.buscarHistorico();
        }
      }
    });
  }

  componentDidUpdate(prevProps) {
    // Carregar histórico quando a aba mudar para histórico
    if (this.props.activeTab === 'historico' && prevProps.activeTab !== 'historico') {
      this.syncControladorSelecionado(() => {
        if (this.isHistoricoPronto()) {
          this.buscarHistorico();
        }
      });
    }

    // Se o refreshKey mudou, recarrega conforme seleção atual
    if (prevProps.refreshKey !== this.props.refreshKey) {
      this.syncControladorSelecionado(() => {
        if (this.isHistoricoPronto()) {
          this.carregarDados();
          if (this.props.activeTab === 'historico') {
            this.buscarHistorico();
          }
        }
      });
      return;
    }

    // Se o controlador salvo globalmente mudou fora do refreshKey, sincroniza
    const next = this.getControladorSelecionadoNormalizado(getConfig('cont'));
    const current = this.getControladorSelecionadoNormalizado(this.state.controladorSelecionado);
    if (next !== current) {
      this.syncControladorSelecionado(() => {
        if (this.isHistoricoPronto()) {
          this.carregarDados();
          if (this.props.activeTab === 'historico') {
            this.buscarHistorico();
          }
        }
      });
    }
  }

  componentWillUnmount() {
    if (this.state.intervalId) {
      clearInterval(this.state.intervalId);
    }
  }

  carregarDados() {
    const { controladorSelecionado } = this.state;
    if (!this.isHistoricoPronto(controladorSelecionado)) return;

    this.props.getLeituraAtual(controladorSelecionado);
    this.props.getConfig(controladorSelecionado);
    this.props.getEstatisticas(controladorSelecionado);
    this.buscarHistorico();
  }

  handleControladorChange(controladorId) {
    this.setState({ controladorSelecionado: controladorId }, () => {
      if (controladorId) {
        this.carregarDados();
      }
    });
  }

  toggleAutoRefresh() {
    const { autoRefresh, intervalId } = this.state;

    if (autoRefresh) {
      clearInterval(intervalId);
      this.setState({ autoRefresh: false, intervalId: null });
    } else {
      const newIntervalId = setInterval(() => {
        this.carregarDados();
      }, 30000); // 30 segundos

      this.setState({ autoRefresh: true, intervalId: newIntervalId });
    }
  }

  atualizarLeitura() {
    this.carregarDados();
  }

  handleFiltroChange(campo, valor) {
    this.setState(prevState => ({
      filtros: {
        ...prevState.filtros,
        [campo]: valor,
      },
    }));
  }

  buscarHistorico() {
    const { controladorSelecionado, filtros } = this.state;
    if (!this.isHistoricoPronto(controladorSelecionado)) {
      this.props.clearData();
      return;
    }

    // Limpar dados antigos antes de buscar novos
    this.props.clearData();

    // Persistir filtros para uso externo (ex.: impressão a partir da aba Tempo Real)
    try {
      const persist = {
        controladorId: controladorSelecionado,
        filtros,
        savedAt: new Date().toISOString(),
      };
      localStorage.setItem('pluviometro_filtros', JSON.stringify(persist));
    } catch (e) {}

    // Fazer nova busca
    this.props.getHistorico(controladorSelecionado, filtros);
  }

  renderTempoReal() {
    const { leituraAtual } = this.props;
    const { controladorSelecionado } = this.state;

    const temDados =
      this.isHistoricoPronto(controladorSelecionado) && leituraAtual;

    // Função para determinar cor por intensidade de chuva
    const colorFor = mm => {
      if (mm <= 1.25) return 'rgba(40,167,69,0.35)'; // verde fraca
      if (mm <= 6.25) return 'rgba(255,193,7,0.45)'; // amarela moderada
      if (mm <= 12.5) return 'rgba(255,159,64,0.55)'; // laranja forte
      return 'rgba(220,53,69,0.55)'; // vermelha muito forte
    };
    const borderFor = mm => {
      if (mm <= 1.25) return 'rgba(40,167,69,0.8)';
      if (mm <= 6.25) return 'rgba(255,193,7,0.9)';
      if (mm <= 12.5) return 'rgba(255,159,64,0.95)';
      return 'rgba(220,53,69,0.95)';
    };

    // Labels dos buckets de 15 minutos
    const bucketLabels = ['00-15', '15-30', '30-45', '45-60'];

    // Usar dados dos buckets calculados no backend ou zeros
    let bucketsMm = [0, 0, 0, 0];
    let eventosRecentes = [];

    if (temDados && leituraAtual.buckets_15min) {
      // Usar dados dos buckets calculados no backend
      bucketsMm = leituraAtual.buckets_15min.map(bucket => Number(bucket.mm.toFixed(2)));

      // Criar eventos recentes baseado nos buckets
      eventosRecentes = leituraAtual.buckets_15min
        .map((bucket, index) => ({
          periodo: bucketLabels[index],
          minuto_inicio: bucket.inicio,
          minuto_fim: bucket.fim,
          milimetros: Number(bucket.mm.toFixed(2)),
          pulsos: bucket.pulsos,
          registros: bucket.registros,
        }))
        .filter(e => e.milimetros > 0)
        .reverse(); // Mais recentes primeiro
    }

    const barColors = bucketsMm.map(mm => colorFor(mm));
    const barBorders = bucketsMm.map(mm => borderFor(mm));

    // Calcular chuva acumulada - sempre crescente
    const acumulado = [];
    let somaAcumulada = 0;
    bucketsMm.forEach((val, idx) => {
      somaAcumulada += val;
      acumulado[idx] = Number(somaAcumulada.toFixed(2));
    });

    const bucketChartData = {
      labels: bucketLabels,
      datasets: [
        {
          type: 'bar',
          label: this.isHistoricoPronto(controladorSelecionado)
              ? `Chuva por 15 minutos - ${moment().format('HH')}h (${moment().format('DD/MM')})`
              : 'Aguardando seleção de controlador',
          data: bucketsMm.map(v => Number(v.toFixed(2))),
          backgroundColor: barColors,
          borderColor: barBorders,
          borderWidth: 1,
          barThickness: 22,
          yAxisID: 'y-axis-1',
        },
        {
          type: 'line',
          label: 'Chuva Acumulada (mm)',
          data: acumulado,
          borderColor: '#007bff',
          backgroundColor: 'rgba(0,123,255,0.1)',
          borderWidth: 2,
          fill: false,
          pointRadius: 3,
          pointHoverRadius: 5,
          yAxisID: 'y-axis-2',
        },
      ],
    };
    // Calcular escala máxima unificada
    const maxIntensidade = Math.max(...bucketsMm, 0);
    const maxAcumulado = Math.max(...acumulado, 0);
    const maxEscala = Math.max(maxIntensidade, maxAcumulado);
    // Adicionar margem mínima e arredondar para múltiplos de 0.5 para evitar números como 3.3000000000003
    // Se já é múltiplo de 0.5, adiciona apenas 0.5. Senão, arredonda para o próximo múltiplo de 0.5
    let maxEscalaComMargem;
    if (maxEscala > 0) {
      const proximoMultiplo = Math.ceil(maxEscala / 0.5) * 0.5;
      maxEscalaComMargem = maxEscala === proximoMultiplo ? maxEscala + 0.5 : proximoMultiplo;
    } else {
      maxEscalaComMargem = 1;
    }

    const bucketChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: false } },
      scales: {
        xAxes: [
          {
            gridLines: { display: false },
            ticks: { fontColor: '#666', fontSize: 11 },
          },
        ],
        yAxes: [
          {
            id: 'y-axis-1',
            type: 'linear',
            position: 'left',
            gridLines: { color: 'rgba(0,0,0,0.06)' },
            ticks: {
              fontColor: '#666',
              fontSize: 11,
              beginAtZero: true,
              max: maxEscalaComMargem,
              callback: value => `${value} mm`,
            },
            scaleLabel: {
              display: true,
              labelString: 'Intensidade (mm)',
              fontColor: '#333',
              fontStyle: 'bold',
              fontSize: 12,
            },
          },
          {
            id: 'y-axis-2',
            type: 'linear',
            position: 'right',
            gridLines: { display: false },
            ticks: {
              fontColor: '#007bff',
              fontSize: 11,
              beginAtZero: true,
              max: maxEscalaComMargem,
              callback: value => `${value} mm`,
            },
            scaleLabel: {
              display: true,
              labelString: 'Acumulado (mm)',
              fontColor: '#007bff',
              fontStyle: 'bold',
              fontSize: 12,
            },
          },
        ],
      },
      tooltips: {
        mode: 'index',
        intersect: false,
        callbacks: {
          label: (tooltipItem, data) => {
            const dataset = data.datasets[tooltipItem.datasetIndex];
            return `${dataset.label}: ${tooltipItem.value} mm`;
          },
        },
      },
    };

    // Eventos já foram calculados acima baseado nos buckets do backend

    return (
      <div className="row" style={{ height: 'calc(100vh - 160px)', minHeight: '600px' }}>
        <div className="col-md-12" style={{ height: '100%' }}>
          <div className="card" style={{ height: '100%' }}>
            <div className="card-body" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                {leituraAtual && leituraAtual.observacao && (
                  <div className="alert alert-warning mb-3">
                    <i className="fa fa-exclamation-triangle" /> {leituraAtual.observacao}
                  </div>
                )}

                {/* KPIs em tons neutros */}
                <div
                  style={{ backgroundColor: '#f8f9fa', padding: '8px 12px', borderRadius: '8px', marginBottom: '12px' }}
                >
                  <div className="row">
                    <div className="col-md-4">
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'flex-start',
                          alignItems: 'center',
                          height: '100%',
                          padding: '4px',
                        }}
                      >
                        <button
                          className="btn btn-primary"
                          onClick={this.atualizarLeitura}
                          style={{ marginRight: '10px' }}
                        >
                          <i className="fa fa-sync" /> Atualizar
                        </button>
                        <TooltipWrapper
                          placement="top"
                          tooltipText="O sistema coleta dados a cada 2 minutos e agrega visualmente em 15 min. Use Atualizar para refletir em tela."
                        >
                          <i
                            className="fa fa-question-circle text-muted"
                            aria-label="Ajuda sobre atualização automática"
                            style={{ cursor: 'help', fontSize: 16 }}
                          />
                        </TooltipWrapper>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div
                        style={{
                          backgroundColor: '#ffffff',
                          border: '1px solid',
                          borderRadius: '8px',
                          padding: '6px 8px',
                          textAlign: 'center',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        }}
                      >
                        <div style={{ fontSize: '1.4rem', fontWeight: 'bold', marginBottom: '2px' }}>
                          {leituraAtual && leituraAtual.milimetros_chuva
                            ? leituraAtual.milimetros_chuva.toFixed(2)
                            : '0.00'}{' '}
                          mm
                        </div>
                        <div style={{ color: '#6c757d', fontSize: '0.8rem' }}>Chuva Atual</div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div
                        style={{
                          backgroundColor: '#ffffff',
                          border: '1px solid',
                          borderRadius: '8px',
                          padding: '6px 8px',
                          textAlign: 'center',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        }}
                      >
                        <div style={{ fontSize: '1.4rem', fontWeight: 'bold', marginBottom: '2px' }}>
                          {leituraAtual && leituraAtual.timestamp
                            ? moment(leituraAtual.timestamp).format('HH:mm:ss')
                            : '--:--:--'}
                        </div>
                        <div style={{ color: '#6c757d', fontSize: '0.8rem' }}>Última Leitura</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Gráfico: Chuva por 15 minutos na hora atual */}
                <div className="row mt-2" style={{ flex: 1, minHeight: 0 }}>
                  <div className="col-md-9" style={{ height: '100%' }}>
                    <div className="card border-0 shadow-sm" style={{ height: '100%' }}>
                      <div className="card-body" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        {/* Legenda de cores */}
                        <div
                          className="mb-2"
                          style={{
                            backgroundColor: '#f8f9fa',
                            borderRadius: '8px',
                            padding: '8px 12px',
                            border: '1px solid #e3e6f0',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              gap: 16,
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexWrap: 'wrap',
                              fontSize: '13px',
                              fontWeight: 'bold',
                              color: '#333',
                            }}
                          >
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              <span
                                style={{
                                  width: 12,
                                  height: 12,
                                  borderRadius: 2,
                                  background: 'rgba(40,167,69,0.35)',
                                  border: '1px solid rgba(0,0,0,0.15)',
                                }}
                              />
                              Fraca (≤ 1.25 mm)
                            </span>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              <span
                                style={{
                                  width: 12,
                                  height: 12,
                                  borderRadius: 2,
                                  background: 'rgba(255,193,7,0.45)',
                                  border: '1px solid rgba(0,0,0,0.15)',
                                }}
                              />
                              Moderada (≤ 6.25 mm)
                            </span>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              <span
                                style={{
                                  width: 12,
                                  height: 12,
                                  borderRadius: 2,
                                  background: 'rgba(255,159,64,0.55)',
                                  border: '1px solid rgba(0,0,0,0.15)',
                                }}
                              />
                              Forte (≤ 12.5 mm)
                            </span>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              <span
                                style={{
                                  width: 12,
                                  height: 12,
                                  borderRadius: 2,
                                  background: 'rgba(220,53,69,0.55)',
                                  border: '1px solid rgba(0,0,0,0.15)',
                                }}
                              />
                              Muito Forte (&gt; 12.5 mm)
                            </span>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              <span
                                style={{
                                  width: 12,
                                  height: 2,
                                  borderRadius: 1,
                                  background: '#007bff',
                                  border: 'none',
                                }}
                              />
                              Acumulada
                            </span>
                          </div>
                        </div>
                        <div
                          style={{
                            flex: 1,
                            minHeight: 0,
                            position: 'relative',
                            backgroundColor: '#ffffff',
                            border: '1px solid #dee2e6',
                            borderRadius: '4px',
                            padding: '15px',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                          }}
                        >
                          <Bar data={bucketChartData} options={bucketChartOptions} />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3" style={{ height: '100%' }}>
                    <div
                      className="card border-0"
                      style={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        backgroundColor: '#ffffff',
                        borderRadius: '12px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                        border: '1px solid #e3e6f0',
                      }}
                    >
                      <div
                        className="card-header border-0"
                        style={{
                          backgroundColor: '#f8f9fa',
                          borderRadius: '12px 12px 0 0',
                          borderBottom: '1px solid #e3e6f0',
                          paddingTop: '8px',
                          paddingBottom: '8px',
                          paddingLeft: '12px',
                          paddingRight: '12px',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div
                            style={{
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              backgroundColor: '#28a745',
                            }}
                          />
                          <strong style={{ color: '#495057', fontSize: '14px' }}>Marcações recentes</strong>
                        </div>
                      </div>
                      <div
                        className="card-body"
                        style={{ padding: '15px', flex: 1, overflowY: 'auto', backgroundColor: '#ffffff' }}
                      >
                        {eventosRecentes.length > 0 ? (
                          <div className="table-responsive">
                            <table className="table table-sm mb-0">
                              <thead>
                                <tr>
                                  <th className="text-muted">Período</th>
                                  <th className="text-muted text-center">Pulsos</th>
                                  <th className="text-muted text-right" style={{ fontWeight: 'bold' }}>
                                    Milímetros
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {eventosRecentes.map((e, i) => {
                                  const mm = e.milimetros;
                                  const bg = (() => {
                                    if (mm <= 1.25) return 'rgba(40,167,69,0.35)'; // verde igual gráfico
                                    if (mm <= 6.25) return 'rgba(255,193,7,0.45)'; // amarelo igual gráfico
                                    if (mm <= 12.5) return 'rgba(255,159,64,0.55)'; // laranja igual gráfico
                                    return 'rgba(220,53,69,0.55)'; // vermelho igual gráfico
                                  })();
                                  return (
                                    <tr key={i}>
                                      <td>
                                        <strong>{e.periodo}</strong>
                                      </td>
                                      <td className="text-center">{e.pulsos}</td>
                                      <td
                                        className="text-right"
                                        style={{
                                          backgroundColor: bg,
                                          fontWeight: 'bold',
                                        }}
                                      >
                                        {mm.toFixed(2)}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <small className="text-muted">Sem eventos na hora atual.</small>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Função para determinar cor por intensidade de chuva
  getColorByIntensity = mm => {
    if (mm <= 1.25) return 'rgba(40,167,69,0.35)'; // verde fraca
    if (mm <= 6.25) return 'rgba(255,193,7,0.45)'; // amarela moderada
    if (mm <= 12.5) return 'rgba(255,159,64,0.55)'; // laranja forte
    return 'rgba(220,53,69,0.55)'; // vermelha muito forte
  };

  renderHistorico() {
    const { historico } = this.props;
    const { filtros } = this.state;

    // Verificação defensiva para historico
    const dadosHistoricoOriginal = Array.isArray(historico) ? historico : (historico && historico.dados) || [];

    // Para o gráfico: ordem cronológica (mais antigo à esquerda, mais recente à direita)
    const dadosHistoricoGrafico = [...dadosHistoricoOriginal].sort((a, b) => {
      // Usar moment.js para comparação mais robusta
      const momentA = moment(
        `${a.data} ${String(a.hora || 0).padStart(2, '0')}:${String(a.minuto || 0).padStart(2, '0')}`,
        'YYYY-MM-DD HH:mm',
      );
      const momentB = moment(
        `${b.data} ${String(b.hora || 0).padStart(2, '0')}:${String(b.minuto || 0).padStart(2, '0')}`,
        'YYYY-MM-DD HH:mm',
      );

      // Ordem crescente: mais antigo primeiro (esquerda), mais recente depois (direita)
      return momentA.diff(momentB);
    });

    console.log(
      '[FRONTEND_DEBUG] Dados ordenados para gráfico (antigo->recente):',
      dadosHistoricoGrafico.map(d => ({
        data: d.data,
        hora: d.hora,
        minuto: d.minuto,
        momentStr: moment(
          `${d.data} ${String(d.hora || 0).padStart(2, '0')}:${String(d.minuto || 0).padStart(2, '0')}`,
          'YYYY-MM-DD HH:mm',
        ).format('YYYY-MM-DD HH:mm'),
      })),
    );

    // Para a tabela: ordem reversa (mais recente no topo)
    const dadosHistorico = [...dadosHistoricoOriginal].sort((a, b) => {
      const dataA = moment(`${a.data} ${a.hora || '00'}:${a.minuto || '00'}`);
      const dataB = moment(`${b.data} ${b.hora || '00'}:${b.minuto || '00'}`);
      return dataB.diff(dataA);
    });

    // KPIs do período
    const totalChuvaPeriodo = dadosHistorico.reduce((acc, it) => acc + (it.total_chuva || 0), 0);
    const maiorLeituraPeriodo = dadosHistorico.reduce((acc, it) => Math.max(acc, it.total_chuva || 0), 0);
    // Calcular média de chuva de acordo com o agrupamento
    const mediaChuvaPeriodo = dadosHistorico.length > 0 ? totalChuvaPeriodo / dadosHistorico.length : 0;

    // Calcular chuva acumulada para o histórico - sempre crescente
    const acumuladoHistorico = [];
    let somaAcumuladaHistorico = 0;
    dadosHistoricoGrafico.forEach((item, idx) => {
      somaAcumuladaHistorico += item.total_chuva || 0;
      acumuladoHistorico[idx] = Number(somaAcumuladaHistorico.toFixed(2));
    });

    const chartData = {
      labels: dadosHistoricoGrafico.map(item => {
        if (filtros.agrupamento === 'dia') {
          return moment(item.data).format('DD/MM');
        } else if (filtros.agrupamento === 'mes') {
          return moment(item.data).format('MM/YYYY');
        } else if (['15min', '30min', '60min'].includes(filtros.agrupamento)) {
          // Calcular horário do fim do período baseado no agrupamento
          const inicioMoment = moment(
            `${item.data} ${String(item.hora || 0).padStart(2, '0')}:${String(item.minuto || 0).padStart(2, '0')}`,
            'YYYY-MM-DD HH:mm',
          );
          let fimMoment;

          if (filtros.agrupamento === '15min') {
            fimMoment = inicioMoment.clone().add(15, 'minutes');
          } else if (filtros.agrupamento === '30min') {
            fimMoment = inicioMoment.clone().add(30, 'minutes');
          } else if (filtros.agrupamento === '60min') {
            fimMoment = inicioMoment.clone().add(60, 'minutes');
          }

          return `${fimMoment.format('DD/MM')} ${fimMoment.format('HH:mm')}`;
        } else {
          // Para agrupamento por hora, mostrar fim da hora (ex: 10h -> 11h)
          const inicioMoment = moment(`${item.data} ${String(item.hora || 0).padStart(2, '0')}:00`, 'YYYY-MM-DD HH:mm');
          const fimMoment = inicioMoment.clone().add(1, 'hour');
          return `${fimMoment.format('DD/MM')} ${fimMoment.format('HH')}h`;
        }
      }),
      datasets: [
        {
          type: 'bar',
          label: 'Chuva (mm)',
          data: dadosHistoricoGrafico.map(item => item.total_chuva),
          backgroundColor: dadosHistoricoGrafico.map(item => this.getColorByIntensity(item.total_chuva || 0)),
          borderColor: dadosHistoricoGrafico.map(item => {
            const bg = this.getColorByIntensity(item.total_chuva || 0);
            return bg
              .replace('0.35)', '0.8)')
              .replace('0.45)', '0.9)')
              .replace('0.55)', '0.95)');
          }),
          borderWidth: 1,
          yAxisID: 'y-axis-1',
        },
        {
          type: 'line',
          label: 'Chuva Acumulada (mm)',
          data: acumuladoHistorico,
          borderColor: '#007bff',
          backgroundColor: 'rgba(0,123,255,0.1)',
          borderWidth: 2,
          fill: false,
          pointRadius: 2,
          pointHoverRadius: 4,
          yAxisID: 'y-axis-2',
        },
      ],
    };

    // Calcular escala máxima unificada para o histórico
    const maxIntensidadeHistorico = Math.max(...dadosHistoricoGrafico.map(item => item.total_chuva || 0), 0);
    const maxAcumuladoHistorico = Math.max(...acumuladoHistorico, 0);
    const maxEscalaHistorico = Math.max(maxIntensidadeHistorico, maxAcumuladoHistorico);
    // Adicionar margem mínima e arredondar para múltiplos de 0.5 para evitar números como 3.3000000000003
    // Se já é múltiplo de 0.5, adiciona apenas 0.5. Senão, arredonda para o próximo múltiplo de 0.5
    let maxEscalaHistoricoComMargem;
    if (maxEscalaHistorico > 0) {
      const proximoMultiplo = Math.ceil(maxEscalaHistorico / 0.5) * 0.5;
      maxEscalaHistoricoComMargem = maxEscalaHistorico === proximoMultiplo ? maxEscalaHistorico + 0.5 : proximoMultiplo;
    } else {
      maxEscalaHistoricoComMargem = 1;
    }

    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: false } },
      scales: {
        xAxes: [
          {
            gridLines: { display: false },
            ticks: { fontColor: '#666', fontSize: 11 },
          },
        ],
        yAxes: [
          {
            id: 'y-axis-1',
            type: 'linear',
            position: 'left',
            gridLines: { color: 'rgba(0,0,0,0.06)' },
            ticks: {
              fontColor: '#666',
              fontSize: 11,
              beginAtZero: true,
              max: maxEscalaHistoricoComMargem,
              callback: value => `${value} mm`,
            },
            scaleLabel: {
              display: true,
              labelString: 'Intensidade (mm)',
              fontColor: '#333',
              fontStyle: 'bold',
              fontSize: 12,
            },
          },
          {
            id: 'y-axis-2',
            type: 'linear',
            position: 'right',
            gridLines: { display: false },
            ticks: {
              fontColor: '#007bff',
              fontSize: 11,
              beginAtZero: true,
              max: maxEscalaHistoricoComMargem,
              callback: value => `${value} mm`,
            },
            scaleLabel: {
              display: true,
              labelString: 'Acumulado (mm)',
              fontColor: '#007bff',
              fontStyle: 'bold',
              fontSize: 12,
            },
          },
        ],
      },
      tooltips: {
        mode: 'index',
        intersect: false,
        callbacks: {
          label: (tooltipItem, data) => {
            const dataset = data.datasets[tooltipItem.datasetIndex];
            return `${dataset.label}: ${tooltipItem.value} mm`;
          },
        },
      },
    };

    return (
      <div className="row">
        <div className="col-md-12">
          <div className="card">
            <div className="card-body">
              <div className="pluviometro-historico-panel">
                <div className="pluviometro-historico-panel-row">
                  <div className="pluviometro-historico-filters">
                    <div className="pluviometro-historico-filter-field">
                      <label>Data Início</label>
                      <input
                        type="date"
                        className="form-control"
                        value={filtros.dataInicio}
                        onChange={e => this.handleFiltroChange('dataInicio', e.target.value)}
                      />
                    </div>
                    <div className="pluviometro-historico-filter-field">
                      <label>Data Fim</label>
                      <input
                        type="date"
                        className="form-control"
                        value={filtros.dataFim}
                        onChange={e => this.handleFiltroChange('dataFim', e.target.value)}
                      />
                    </div>
                    <div className="pluviometro-historico-filter-field">
                      <label>Agrupamento</label>
                      <select
                        className="form-control"
                        value={filtros.agrupamento}
                        onChange={e => this.handleFiltroChange('agrupamento', e.target.value)}
                      >
                        <option value="15min">15 minutos</option>
                        <option value="30min">30 minutos</option>
                        <option value="60min">60 minutos</option>
                        <option value="dia">Por Dia</option>
                        <option value="mes">Por Mês</option>
                      </select>
                    </div>
                    <div className="pluviometro-historico-filter-action">
                      <button className="btn btn-primary pluviometro-historico-search-btn" onClick={this.buscarHistorico}>
                        <i className="fa fa-search" /> Buscar
                      </button>
                    </div>
                  </div>

                  <div className="pluviometro-historico-kpis">
                    <div className="pluviometro-historico-kpi" title="Total no período">
                      <strong className="is-total">{totalChuvaPeriodo.toFixed(2)} mm</strong>
                      <span>Total</span>
                    </div>
                    <div className="pluviometro-historico-kpi" title="Maior leitura">
                      <strong className="is-max">{maiorLeituraPeriodo.toFixed(2)} mm</strong>
                      <span>Máxima</span>
                    </div>
                    <div className="pluviometro-historico-kpi" title="Média de chuva">
                      <strong className="is-avg">{mediaChuvaPeriodo.toFixed(2)} mm</strong>
                      <span>Média</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Gráfico */}
              <div
                style={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #dee2e6',
                  borderRadius: '4px',
                  padding: '15px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                }}
              >
                {/* Legenda de cores */}
                <div
                  className="mb-3"
                  style={{
                    display: 'flex',
                    gap: 20,
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexWrap: 'wrap',
                    fontSize: '13px',
                    color: '#555',
                  }}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <span
                      style={{
                        width: 14,
                        height: 14,
                        borderRadius: 3,
                        background: 'rgba(40,167,69,0.35)',
                        border: '1px solid rgba(0,0,0,0.15)',
                      }}
                    />
                    Fraca (≤ 1.25 mm)
                  </span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <span
                      style={{
                        width: 14,
                        height: 14,
                        borderRadius: 3,
                        background: 'rgba(255,193,7,0.45)',
                        border: '1px solid rgba(0,0,0,0.15)',
                      }}
                    />
                    Moderada (≤ 6.25 mm)
                  </span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <span
                      style={{
                        width: 14,
                        height: 14,
                        borderRadius: 3,
                        background: 'rgba(255,159,64,0.55)',
                        border: '1px solid rgba(0,0,0,0.15)',
                      }}
                    />
                    Forte (≤ 12.5 mm)
                  </span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <span
                      style={{
                        width: 14,
                        height: 14,
                        borderRadius: 3,
                        background: 'rgba(220,53,69,0.55)',
                        border: '1px solid rgba(0,0,0,0.15)',
                      }}
                    />
                    Muito Forte (&gt; 12.5 mm)
                  </span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <span
                      style={{
                        width: 14,
                        height: 2,
                        borderRadius: 1,
                        background: '#007bff',
                        border: 'none',
                      }}
                    />
                    Acumulada
                  </span>
                </div>
                <div style={{ height: '48vh', minHeight: 300 }}>
                  <Bar data={chartData} options={chartOptions} />
                </div>

                {/* Tabela de dados */}
                <div
                  className="table-responsive mt-4"
                  style={{
                    maxHeight: '48vh',
                    overflowY: 'auto',
                    backgroundColor: '#ffffff',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  }}
                >
                  <table className="table table-sm mb-0">
                    <thead>
                      <tr style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
                        <th className="text-muted">Data/Hora</th>
                        <th className="text-muted text-right">Chuva (mm)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dadosHistorico.length === 0 ? (
                        <tr>
                          <td colSpan="2" className="text-center text-muted py-4">
                            Nenhum registro no período selecionado
                          </td>
                        </tr>
                      ) : (
                        dadosHistorico.map((item, index) => {
                        const chuvaValue = item.total_chuva || 0;
                        const bgColor = this.getColorByIntensity(chuvaValue);

                        let dataHoraFormatada;
                        if (filtros.agrupamento === 'dia') {
                          dataHoraFormatada = moment(item.data).format('DD/MM/YYYY');
                        } else if (filtros.agrupamento === 'mes') {
                          dataHoraFormatada = moment(item.data).format('MM/YYYY');
                        } else if (['15min', '30min', '60min'].includes(filtros.agrupamento)) {
                          // Calcular horário do fim do período baseado no agrupamento
                          const inicioMoment = moment(
                            `${item.data} ${String(item.hora || 0).padStart(2, '0')}:${String(
                              item.minuto || 0,
                            ).padStart(2, '0')}`,
                            'YYYY-MM-DD HH:mm',
                          );
                          let fimMoment;

                          if (filtros.agrupamento === '15min') {
                            fimMoment = inicioMoment.clone().add(15, 'minutes');
                          } else if (filtros.agrupamento === '30min') {
                            fimMoment = inicioMoment.clone().add(30, 'minutes');
                          } else if (filtros.agrupamento === '60min') {
                            fimMoment = inicioMoment.clone().add(60, 'minutes');
                          }

                          dataHoraFormatada = `${fimMoment.format('DD/MM/YYYY')} ${fimMoment.format('HH:mm')}`;
                        } else {
                          // Para agrupamento por hora, mostrar fim da hora
                          const inicioMoment = moment(
                            `${item.data} ${String(item.hora || 0).padStart(2, '0')}:00`,
                            'YYYY-MM-DD HH:mm',
                          );
                          const fimMoment = inicioMoment.clone().add(1, 'hour');
                          dataHoraFormatada = `${fimMoment.format('DD/MM/YYYY')} ${fimMoment.format('HH:mm')}`;
                        }

                        return (
                          <tr key={index}>
                            <td>{dataHoraFormatada}</td>
                            <td
                              className="text-right"
                              style={{
                                backgroundColor: bgColor,
                                fontWeight: 'bold',
                              }}
                            >
                              {chuvaValue.toFixed(2)}
                            </td>
                          </tr>
                        );
                      })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { activeTab } = this.props;

    return (
      <div>
        <div>
          {activeTab === 'tempo-real' && this.renderTempoReal()}
          {activeTab === 'historico' && this.renderHistorico()}
        </div>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  leituraAtual: state.pluviometro.leituraAtual,
  historico: state.pluviometro.historico,
  estatisticas: state.pluviometro.estatisticas,
  config: state.pluviometro.config,
});

const mapDispatchToProps = dispatch => bindActionCreators(pluviometroActions, dispatch);

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(PluviometroList);
